import React from 'react';
import { X, AlertCircle, Download } from 'lucide-react';
import { InlineLoadingState } from './LoadingSpinner';
import { createPortal } from 'react-dom';
import type { DocumentSearchHit } from '../utils/documentSearchNavigation';
import { PdfDocumentViewer } from './PdfDocumentViewer';
import { SpreadsheetViewer } from './SpreadsheetViewer';
import { PdfPrintButton } from './PdfPrintButton';
import { PdfMailShareButton } from './PdfMailShareButton';
import { spreadsheetPreviewKind } from '../utils/spreadsheetPreview';

const KIND_LABEL: Record<DocumentSearchHit['kind'], string> = {
  invoice: 'Angebot / Rechnung',
  lead_attachment: 'Anhang',
  organization: 'Organisationsdatei',
  ausschreibung: 'Ausschreibungsunterlage',
};

function isPdf(filename: string, mimeType?: string | null): boolean {
  if (mimeType?.toLowerCase().includes('pdf')) return true;
  return filename.toLowerCase().endsWith('.pdf');
}

type Props = {
  document: DocumentSearchHit | null;
  onClose: () => void;
};

export const DocumentPreviewModal: React.FC<Props> = ({ document, onClose }) => {
  const [url, setUrl] = React.useState<string | null>(null);
  const [mimeType, setMimeType] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!document) {
      setUrl(null);
      setMimeType(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setUrl(null);
    setError(null);
    setMimeType(null);

    fetch(
      `/api/search/documents/${encodeURIComponent(document.kind)}/${encodeURIComponent(document.id)}/signed`,
      { credentials: 'include' },
    )
      .then(async (r) => {
        const raw = (await r.json().catch(() => ({}))) as {
          url?: string;
          filename?: string;
          mime_type?: string;
          error?: string;
        };
        if (!r.ok) throw new Error(raw.error ?? 'Vorschau nicht möglich.');
        if (!raw.url) throw new Error('Keine URL erhalten.');
        return raw;
      })
      .then((raw) => {
        if (!cancelled) {
          setUrl(raw.url!);
          setMimeType(raw.mime_type ?? null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [document?.id, document?.kind]);

  React.useEffect(() => {
    if (!document) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [document, onClose]);

  if (!document) return null;

  const spreadsheetKind = isPdf(document.filename, mimeType) ? null : spreadsheetPreviewKind(document.filename, mimeType);
  const canPreview = isPdf(document.filename, mimeType) || spreadsheetKind !== null;

  return createPortal(
    <div
      className="fixed inset-0 z-[350] flex items-center justify-center bg-black/50 p-4 md:p-8"
      onClick={onClose}
    >
      <div
        className="relative flex flex-col w-full max-w-5xl max-h-[92vh] bg-app-surface rounded-xl shadow-2xl border border-app-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-app-border shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {KIND_LABEL[document.kind]}
            </p>
            <h2
              className="text-sm font-semibold text-app-text truncate"
              title={document.filename}
            >
              {document.filename || 'Dokument'}
            </h2>
            {document.subtitle && document.subtitle !== '—' && (
              <p className="text-xs text-app-text-subtle truncate">{document.subtitle}</p>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-1">
            {url && canPreview && !spreadsheetKind ? (
              <PdfMailShareButton
                url={url}
                filename={document.filename || 'dokument.pdf'}
                variant="icon"
                onBeforeOpen={onClose}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text transition"
              />
            ) : null}
            {url && canPreview && !spreadsheetKind ? (
              <PdfPrintButton
                url={url}
                variant="icon"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text transition"
              />
            ) : null}
            {url && (
              <a
                href={url}
                download={document.filename || 'dokument'}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text transition"
                title="Herunterladen"
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="w-4 h-4" />
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text transition"
              aria-label="Schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 bg-app-surface-muted flex flex-col">
          {loading && (
            <div className="flex-1 grid place-items-center p-10">
              <InlineLoadingState label="Wird geladen…" />
            </div>
          )}
          {!loading && error && (
            <div className="flex-1 flex items-start gap-3 p-6">
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <p>{error}</p>
              </div>
            </div>
          )}
          {!loading && !error && url && spreadsheetKind && (
            <SpreadsheetViewer
              url={url}
              filename={document.filename}
              kind={spreadsheetKind}
              className="min-h-[60vh] w-full md:min-h-[72vh]"
            />
          )}
          {!loading && !error && url && canPreview && !spreadsheetKind && (
            <PdfDocumentViewer
              url={url}
              title={document.filename}
              className="min-h-[60vh] w-full md:min-h-[72vh]"
            />
          )}
          {!loading && !error && url && !canPreview && (
            <div className="flex-1 grid place-items-center p-10 text-center">
              <div className="space-y-3">
                <p className="text-sm text-app-text-muted">
                  Diese Datei kann nicht in der Vorschau angezeigt werden.
                </p>
                <a
                  href={url}
                  download={document.filename || 'dokument'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
                >
                  <Download className="w-4 h-4" />
                  Herunterladen
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    window.document.body,
  );
};
